import { IconChevronLeft } from '@tabler/icons-react'
import { getAllTeachers, getArcProjectTeachers, getProject } from 'api'
import { Box, Image, Paper, ProjectHeader, RemoveConfirmModal, Skeleton, Tabs, Text, TextLink } from 'components'
import { hasPermission, useFeatureToggle, useMantineTheme, useMediaQuery } from 'hooks'
import { setDocumentTitle } from 'utils/documentTitle'
import { useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { ProjectViewContext } from '../../context/ProjectViewContext'
import { DevelopmentTrackerTab } from '../DevelopmentTrackerTab'
import { ProjectParticipantsTab } from '../ProjectParticipantsTab'
import { ProjectViewContent } from '../ProjectViewContent'
import { ProjectViewSkeleton } from '../ProjectViewSkeleton'
import { TasksTrackerTab } from '../TasksTrackerTab'
import { StudentProjectTrackerTab } from '../../../StudentTaskTracker'
import { useStyles } from './styles'

const ARC_STATUSES_WITH_TRACKER = ['approved', 'finished'] as const

const isAssignedOrCompleted = (status: string): boolean =>
  ARC_STATUSES_WITH_TRACKER.includes(status as typeof ARC_STATUSES_WITH_TRACKER[number])

const DEFAULT_TAB = 'General view'

const TAB_TO_SLUG: Record<string, string> = {
  'General view': 'general-view',
  'Project participants': 'project-participants',
  'Tasks tracker': 'tasks-tracker',
  'Development tracker': 'development-tracker',
}

const SLUG_TO_TAB: Record<string, string> = Object.fromEntries(
  Object.entries(TAB_TO_SLUG).map(([tab, slug]) => [slug, tab]),
)

export const ProjectView = () => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isArcMosaico = useFeatureToggle('arc-mosaico-project') as boolean
  const canManageAssessments = hasPermission(['tutor', 'manager'])
  // AR-898: students get their own read/submit variant of the Tasks tracker tab.
  const isStudentViewer = hasPermission(['student'])
  const showTasksTracker = canManageAssessments || isStudentViewer

  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const backParam = searchParams.get('back')
  const tabParam = searchParams.get('tab')
  const currentTab = (tabParam && SLUG_TO_TAB[tabParam]) || DEFAULT_TAB
  const projectID = id

  const onTabChange = (value: string) => {
    handleTabChange(value)
    if (value === 'Project wall') return // handled by redirect inside handleTabChange
    const next = new URLSearchParams(searchParams)
    const slug = TAB_TO_SLUG[value]
    if (!slug || value === DEFAULT_TAB) {
      next.delete('tab')
    } else {
      next.set('tab', slug)
    }
    setSearchParams(next, { replace: true })
  }

  const {
    project,
    setProject,
    teachers,
    setTeachers,
    setError,
    openedRemoveConfirmationModal,
    closeRemoveConfirmationModal,
    handleRemoveConfirmationClose,
    handleTabChange,
    handleEdit,
    handleRemove,
    handlePublish,
    handleApprove,
    handleReopen,
    handleFinish,
    setLessonsLoading,
    setFinalAssessmentStarted,
    lessonsPagination,
  } = useContext(ProjectViewContext)

  useEffect(() => {
    if (projectID && projectID !== '') {
      const getData = () => {
        // Mark lessons as loading up front; the General view tab owns the actual
        // session-meetings fetch and releases this flag when its request settles.
        // This avoids the duplicate getLessons call that used to race here and
        // flash an empty state before the meetings rendered.
        setLessonsLoading(true)
        getProject(projectID)
          .then(response => {
            if (response.status === 200) {
              setProject(response.data)
              setDocumentTitle(response.data.name)
              setFinalAssessmentStarted(response.data.startFinalAssessment)
              if (isArcMosaico || response.data.kind !== 'template') {
                const fetchTeachers = isArcMosaico ? getArcProjectTeachers : getAllTeachers
                fetchTeachers(projectID)
                  .then(response => {
                    if (response.status === 200) {
                      setTeachers(response.data)
                    } else {
                      setError(response.statusText)
                    }
                  })
                  .catch(err => {
                    setError(err.message)
                  })
              }
            } else {
              setError(response.statusText)
            }
          })
          .catch(err => {
            setError(err.message)
          })
      }
      getData()
    }
  }, [projectID])

  const backlink = useMemo(() => {
    if (!backParam) {
      // Legacy (non-Arc) templates published/in-review return to /projects_library (August behavior).
      const statusLibrary = project?.status === 'published' || project?.status === 'reviewing'
      return !isArcMosaico && project?.kind === 'template' && statusLibrary ? '/projects_library' : '/projects'
    }
    if (backParam.startsWith('/arc_projects') || backParam.startsWith('/projects')) {
      return backParam
    }
    return '/projects'
  }, [backParam, isArcMosaico, project?.status, project?.kind])

  return (
    <>
      {project !== null && (
        <RemoveConfirmModal
          type={project.kind === 'template' ? 'project_template' : 'project'}
          opened={openedRemoveConfirmationModal}
          onClose={closeRemoveConfirmationModal}
          actionCancelConfirm={() => handleRemoveConfirmationClose(project.id)}
        />
      )}
      {project !== null ? (
        <TextLink href={backlink} size='md' iconLeft={<IconChevronLeft />}>
          {t('new_project_view.view_all')}
        </TextLink>
      ) : (
        <Skeleton height={20} width={140} />
      )}
      <Paper radius='md' withBorder shadow='xs' p={0} mt='xs'>
        <Image
          withPlaceholder
          width='100%'
          height={240}
          src={project !== null ? project.coverImage : ''}
          className={classes.root}
        />
        <Box p={isMobile ? 'md' : 'xl'}>
          {project === null && <ProjectViewSkeleton isArcMosaico={isArcMosaico} />}
          {project !== null && (
            <>
              <ProjectHeader
                project={project}
                teachers={teachers}
                // AR-882: pagy count of the (distinct) session-meetings list;
                // project.totalMeetings counts activities for students
                lessonsCount={lessonsPagination?.count}
                handleEdit={handleEdit}
                handleRemove={handleRemove}
                handlePublish={handlePublish}
                handleApprove={handleApprove}
                handleReopen={handleReopen}
                handleFinish={handleFinish}
              />
              <Tabs mt='lg' value={currentTab} onTabChange={onTabChange}>
                <Tabs.List>
                  <Tabs.Tab value='General view'>
                    <Text size='md'>{t('new_project_view.tab_general_view')}</Text>
                  </Tabs.Tab>
                  {!isArcMosaico && project.status !== 'draft' && (
                    <Tabs.Tab value='Project wall'>
                      <Text size='md'>{t('new_project_view.tab_project_wall')}</Text>
                    </Tabs.Tab>
                  )}
                  {isArcMosaico && project.kind !== 'template' && (
                    <Tabs.Tab value='Project participants'>
                      <Text size='md'>{t('new_project_view.tab_project_participants')}</Text>
                    </Tabs.Tab>
                  )}
                  {isArcMosaico
                    && project.kind !== 'template'
                    && showTasksTracker
                    && isAssignedOrCompleted(project.status) && (
                    <Tabs.Tab value='Tasks tracker'>
                      <Text size='md'>{t('new_project_view.tab_tasks_tracker')}</Text>
                    </Tabs.Tab>
                  )}
                  {isArcMosaico
                    && project.kind !== 'template'
                    && isAssignedOrCompleted(project.status) && (
                    <Tabs.Tab value='Development tracker'>
                      <Text size='md'>{t('new_project_view.tab_development_tracker')}</Text>
                    </Tabs.Tab>
                  )}
                </Tabs.List>
                <Tabs.Panel value='General view' pt='md'>
                  <ProjectViewContent />
                </Tabs.Panel>
                <Tabs.Panel value='Project wall' pt='md'>
                  <Text>{t('new_project_view.tab_project_wall_content')}</Text>
                </Tabs.Panel>
                {isArcMosaico && project.kind !== 'template' && (
                  <Tabs.Panel value='Project participants' pt='md'>
                    <ProjectParticipantsTab />
                  </Tabs.Panel>
                )}
                {isArcMosaico
                  && project.kind !== 'template'
                  && showTasksTracker
                  && isAssignedOrCompleted(project.status) && (
                  <Tabs.Panel value='Tasks tracker' pt='md'>
                    {canManageAssessments ? (
                      <TasksTrackerTab projectName={project.name} projectStatus={project.status} />
                    ) : (
                      <StudentProjectTrackerTab projectStatus={project.status} />
                    )}
                  </Tabs.Panel>
                )}
                {isArcMosaico
                  && project.kind !== 'template'
                  && isAssignedOrCompleted(project.status) && (
                  <Tabs.Panel value='Development tracker' pt='md'>
                    <DevelopmentTrackerTab />
                  </Tabs.Panel>
                )}
              </Tabs>
            </>
          )}
        </Box>
      </Paper>
    </>
  )
}
